import { useNavigate } from 'react-router-dom';

import { Box, Button, Stack, Typography } from '@mui/material';

import { useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectProjectSpecialDates,
} from 'state/workflowSlice';

import AddSpecialEventBtn from 'features/user_admin/SpecialDateRequest/AddSpecialEventBtn';
import { SpecialEventHeader } from 'features/user_admin/SpecialDateRequest/SpecialDateRequestTable';
import { projectSpecialEventsUrl } from 'features/user_admin/common';
import { dimmed_white_color } from 'theme/cemTheme';

import FontAwesomeIcon from '../FontAwesomeIcon';
import { StlButton } from '../StlButton/StlButton';
import { useSubscription } from '../hooks/useSubscription';
import { classNames } from '../utils';
import { SpecialEventsDateList } from './SpecialEventsDateList';

import commonStyles from './DateTimeSelector.module.css';
import styles from './SpecialDatesPicker.module.css';

interface ISpecialDatePicker {
  targetDate: string;
  endDate?: string;
  open: boolean;
  narrow: boolean;
  onClose: () => void;
  setNewTargetDate: (value: string | undefined) => void;
  setNewRangeDate?: (start: string, end: string) => void;
  forbiddenDates?: string[];
}

export default function SpecialDatesPicker({
  targetDate,
  endDate,
  onClose,
  narrow,
  open,
  setNewTargetDate,
  setNewRangeDate,
  forbiddenDates,
}: ISpecialDatePicker) {
  const navigate = useNavigate();
  const specialDates = useAppSelector(selectProjectSpecialDates);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const subscription_id = userProject?.subscription_id;
  const project_slug = userProject?.project_slug;
  const { subscription } = useSubscription(subscription_id, project_slug);
  const canAdd = subscription && subscription.user_is_sub_admin;

  const handleAddNew = () => {
    if (!subscription_id || !project_slug) return;
    navigate(projectSpecialEventsUrl(subscription_id, project_slug));
  };

  const handleSelect = (date: string, endDate?: string) => {
    if (endDate && setNewRangeDate) {
      setNewRangeDate(date, endDate);
    } else {
      setNewTargetDate(date);
    }
    onClose();
  };

  return (
    <Stack
      // smoother first render in a version when subsription must be determined
      sx={{ display: open ? 'flex' : 'none' }}
      className={classNames(
        commonStyles.time_picker,
        styles.specialDatePicker,
        narrow && styles.narrow,
        commonStyles.withBorder,
      )}
    >
      <Box display="flex" justifyContent="space-between">
        <Button
          onClick={onClose}
          className={commonStyles.textBtn}
          startIcon={<FontAwesomeIcon name="chevron-left" />}
        >
          <Typography variant="body2">Back</Typography>
        </Button>
        {specialDates?.length > 0 && canAdd && (
          <StlButton
            variant="text"
            icon={<FontAwesomeIcon name="plus" size="0.875rem" gold />}
            width={90}
            value="Add New"
            handleClick={handleAddNew}
            sx={{ margin: 0 }}
          />
        )}
      </Box>
      {specialDates?.length > 0 ? (
        <>
          <Stack>
            <Typography variant="body2">Select a Special Event</Typography>
            <Typography
              color={dimmed_white_color}
              variant="body2"
              fontSize="0.75rem"
            >
              Upcoming events will be selectable after they occur
            </Typography>
          </Stack>
          <SpecialEventsDateList
            open={open}
            allowRange={Boolean(setNewRangeDate)}
            onSelect={handleSelect}
            targetDate={targetDate}
            endDate={endDate}
            forbiddenDates={forbiddenDates}
          />
        </>
      ) : (
        <Stack className={styles.emptySpecialDates}>
          <Stack alignItems="center">
            <SpecialEventHeader lightCol />
            <Typography variant="body2" className={styles.txt}>
              {canAdd
                ? 'Add past or future dates to compare and discover new insights.'
                : 'Talk to your subscription administrator to request special event dates.'}
            </Typography>
          </Stack>
          {canAdd && (
            <AddSpecialEventBtn
              subscriptionId={subscription_id}
              projectId={userProject.project_slug}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}

export function SpecialDatesButton({
  handleClick,
}: {
  handleClick: () => void;
}) {
  return (
    <Button
      fullWidth
      className={classNames(commonStyles.textBtn)}
      onClick={handleClick}
      sx={{ height: '1.5rem' }}
    >
      <FontAwesomeIcon name="clock-rotate-left" sx={{ padding: '6px' }} />
      <Typography variant="body2">Special Events</Typography>
    </Button>
  );
}
